import { createSignal } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { chatStore } from '@/stores/chatStore';

export interface WebSocketMessage {
  type: string;
  data: any;
}

export interface WebSocketConfig {
  url: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private isConnected = createSignal(false);

  constructor(config: WebSocketConfig) {
    this.config = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      ...config,
    };
  }

  connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    try {
      const wsUrl = this.config.url.replace('http://', 'ws://').replace('https://', 'wss://');
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected:', this.config.url);
        this.isConnected[1](true);
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.config.onConnect?.();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
          this.config.onMessage?.(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.isConnected[1](false);
        this.stopHeartbeat();
        this.config.onDisconnect?.();
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.config.onError?.(error);
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.attemptReconnect();
    }
  }

  disconnect(): void {
    this.stopReconnectTimer();
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.isConnected[1](false);
  }

  send(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected. Message not sent:', message);
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'game_state':
        this.handleGameState(message.data);
        break;
      case 'game_update':
        this.handleGameUpdate(message.data);
        break;
      case 'ranking_update':
        this.handleRankingUpdate(message.data);
        break;
      case 'team_progress':
        this.handleTeamProgress(message.data);
        break;
      case 'team_update':
        this.handleTeamUpdate(message.data);
        break;
      case 'answer_result':
        this.handleAnswerResult(message.data);
        break;
      case 'notification':
        this.handleNotification(message.data);
        break;
      case 'session_finished':
      case 'game_finished':
        this.handleSessionFinished(message.data);
        break;
      case 'pong':
        // Heartbeat response
        break;
      // Chat message handlers
      case 'chat_new_message':
        this.handleChatNewMessage(message.data);
        break;
      case 'chat_message_sent':
        this.handleChatMessageSent(message.data);
        break;
      case 'chat_unread_count':
        this.handleChatUnreadCount(message.data);
        break;
      default:
        console.log('Unknown WebSocket message type:', message.type);
    }
  }

  private handleGameState(data: any): void {
    if (data.game) {
      gameStore.updateGame(data.game);
    }
    if (data.teams) {
      gameStore.updateRankingFromWS(data.teams);
    }
  }

  private handleGameUpdate(data: any): void {
    if (data.game) {
      gameStore.updateGame(data.game);
    }
  }

  private handleRankingUpdate(data: any): void {
    if (data.ranking) {
      gameStore.updateRankingFromWS(data.ranking);
    }
  }

  private handleTeamProgress(data: any): void {
    // Update team progress in store
    console.log('Team progress update:', data);
  }

  private handleTeamUpdate(data: any): void {
    if (data.team) {
      gameStore.updateTeam(data.team);
    }
    
    // Handle special events
    if (data.event === 'game_completed') {
      gameStore.setGameCompleted(true);
      gameStore.setNotification({
        type: 'success',
        message: data.message,
        duration: 10000,
      });
    }
  }

  private handleAnswerResult(data: any): void {
    const notificationType = data.is_correct ? 'success' : 'error';
    
    gameStore.setNotification({
      type: notificationType,
      message: data.message,
      duration: 5000,
    });

    // Update progress if correct
    if (data.is_correct && data.assigned_digit) {
      gameStore.addCollectedDigit({
        tile_number: data.tile_number,
        digit: data.assigned_digit,
        points: data.points_awarded,
      });
    }
  }

  private handleNotification(data: any): void {
    const notificationType = data.log_type === 'error' ? 'error' : 'info';

    gameStore.setNotification({
      type: notificationType,
      message: data.message,
      duration: 7000,
    });
  }

  private handleSessionFinished(data: any): void {
    // Update session status to finished - this triggers the blocking modal
    gameStore.updateSession({ status: 'finished' as any });

    // Also update game if provided
    if (data.game) {
      gameStore.updateGame(data.game);
    }

    console.log('Session finished:', data);
  }

  // Chat message handlers
  private handleChatNewMessage(data: any): void {
    // New message from operator
    chatStore.handleNewMessage({
      id: data.id,
      sender_type: data.sender_type,
      content: data.content,
      image_url: data.image_url,
      is_read: data.is_read,
      created_at: data.created_at,
    });
    console.log('New chat message:', data);
  }

  private handleChatMessageSent(data: any): void {
    // Confirmation that our message was sent
    console.log('Chat message sent:', data);
  }

  private handleChatUnreadCount(data: any): void {
    // Update unread count
    chatStore.handleUnreadCount(data.unread_count || 0);
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000); // Send ping every 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= (this.config.maxReconnectAttempts || 10)) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, this.config.reconnectInterval || 3000);
  }

  private stopReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  getConnectionStatus = () => this.isConnected[0];
}

// WebSocket URL helper
function getWebSocketUrl(path: string): string {
  // In production, use current window location for WebSocket
  // In development, use VITE_API_URL or localhost
  const envUrl = import.meta.env?.VITE_API_URL;
  let baseUrl: string;

  if (envUrl !== undefined && envUrl !== '') {
    baseUrl = envUrl;
  } else if (import.meta.env?.DEV) {
    baseUrl = 'http://127.0.0.1:8000';
  } else {
    // Production: use current window location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${path}`;
  }

  return baseUrl.replace('http://', 'ws://').replace('https://', 'wss://') + path;
}

// Game WebSocket service
export function createGameWebSocket(gameId: number): WebSocketService {
  return new WebSocketService({
    url: getWebSocketUrl(`/ws/game/${gameId}/`),
    onConnect: () => {
      console.log('Connected to game WebSocket');
      gameStore.setConnectionStatus(true);
    },
    onDisconnect: () => {
      console.log('Disconnected from game WebSocket');
      gameStore.setConnectionStatus(false);
    },
    onError: (error) => {
      console.error('Game WebSocket error:', error);
      gameStore.setNotification({
        type: 'error',
        message: 'Connection lost. Attempting to reconnect...',
        duration: 5000,
      });
    },
  });
}

// Team WebSocket service
export function createTeamWebSocket(teamId: number): WebSocketService {
  return new WebSocketService({
    url: getWebSocketUrl(`/ws/team/${teamId}/`),
    onConnect: () => {
      console.log('Connected to team WebSocket');
    },
    onDisconnect: () => {
      console.log('Disconnected from team WebSocket');
    },
    onError: (error) => {
      console.error('Team WebSocket error:', error);
    },
  });
}

// Admin WebSocket service (for future admin dashboard)
export function createAdminWebSocket(): WebSocketService {
  return new WebSocketService({
    url: getWebSocketUrl('/ws/admin/'),
    onConnect: () => {
      console.log('Connected to admin WebSocket');
    },
    onDisconnect: () => {
      console.log('Disconnected from admin WebSocket');
    },
  });
}